'use client';

import { useIntl } from 'react-intl';
import React, { Suspense } from 'react';

import { Dialog, type DialogProps } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import type { InstallmentCalculatorProps } from '../InstallmentCalculator/InstallmentCalculator.client';
import { InstallmentCalculatorErrorFallback } from '../fallbacks/InstallmentCalculatorErrorFallback/InstallmentCalculatorErrorFallback.client';
import { InstallmentCalculatorSuspenseFallback } from '../fallbacks/InstallmentCalculatorSuspenseFallback/InstallmentCalculatorSuspenseFallback';
import { LazyCalculator } from '../LazyCalculator/LazyCalculator.client';
import { useInstallmentCalculatorTracking } from '../components/Tracking/hooks/useInstallmentCalculatorTracking/useInstallmentCalculatorTracking';

export type InstallmentCalculatorDialogProps = Omit<DialogProps, 'title' | 'dialogContent'> &
  Omit<InstallmentCalculatorProps, 'layout'>;

export const InstallmentCalculatorDialog = ({
  basePrice,
  hasEditablePrice,
  source,
  ...dialogProps
}: InstallmentCalculatorDialogProps) => {
  const { formatMessage } = useIntl();
  const { trackDialogOpen } = useInstallmentCalculatorTracking();

  React.useEffect(() => {
    if (dialogProps.isOpen) {
      trackDialogOpen();
    }
  }, [trackDialogOpen, dialogProps.isOpen]);

  return (
    <Dialog {...dialogProps} className="min-w-screen" variant="bottomSheet">
      <DialogHeader onClose={dialogProps.onClose}>
        {formatMessage({
          id: 'ic-headline',
          defaultMessage: 'Ratenrechner',
        })}
      </DialogHeader>
      {/* these classes are needed to avoid layout changes during suspense because the HTML dialog has a dynamic width */}
      <DialogContent className="w-full max-w-[800px] min-w-dvw lg:min-w-[800px]">
        <ErrorBoundary
          fallback={(reset) => <InstallmentCalculatorErrorFallback onCloseHandler={reset} />}
        >
          <Suspense fallback={<InstallmentCalculatorSuspenseFallback calculatorLayout="Columns" />}>
            <LazyCalculator
              basePrice={basePrice}
              hasEditablePrice={hasEditablePrice}
              source={source}
              layout="Columns"
            />
          </Suspense>
        </ErrorBoundary>
      </DialogContent>
    </Dialog>
  );
};
